import { useEffect, useState, useRef, useCallback } from 'react'
import { api } from '../utils/api'
import { abTestsApi, ApiAbTest } from '../utils/api'

interface PageViewStat { slug: string; views: number }
interface DayStat { day: string; views: number }
interface RefStat { referer: string | null; views: number }
interface Stats {
  total: number
  byPage: PageViewStat[]
  byDay: DayStat[]
  byReferer: RefStat[]
}

interface Props {
  siteId: string | null
  onClose: () => void
}

const DAYS_OPTIONS = [7, 14, 30, 90]

function TrafficTab({ siteId }: { siteId: string }) {
  const [stats, setStats] = useState<Stats | null>(null)
  const [days, setDays] = useState(30)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    api.get<Stats>(`/sites/${siteId}/analytics?days=${days}`)
      .then(setStats).catch(() => setStats(null)).finally(() => setLoading(false))
  }, [siteId, days])

  const maxDayViews = stats ? Math.max(1, ...stats.byDay.map(d => d.views)) : 1

  return (
    <>
      <div style={{ display: 'flex', gap: '4px', padding: '16px 24px 0', borderBottom: '1px solid var(--border)', paddingBottom: '16px' }}>
        {DAYS_OPTIONS.map(d => (
          <button key={d} onClick={() => setDays(d)}
            style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: days === d ? 'var(--accent)' : 'var(--bg-2)', color: days === d ? '#fff' : 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
            {d}д
          </button>
        ))}
      </div>
      <div style={{ padding: '20px 24px', flex: 1 }}>
        {loading && <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px' }}>Загрузка...</div>}
        {!loading && !stats && <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px' }}>Нет данных</div>}
        {stats && (
          <>
            <div style={{ background: 'var(--bg-2)', borderRadius: '12px', padding: '20px', marginBottom: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '36px', fontWeight: 800, color: 'var(--accent)' }}>{stats.total.toLocaleString('ru-RU')}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>просмотров за {days} дней</div>
            </div>

            {stats.byDay.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' }}>По дням</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '80px' }}>
                  {stats.byDay.map(d => (
                    <div key={d.day} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', minWidth: 0 }} title={`${d.day}: ${d.views}`}>
                      <div style={{ width: '100%', background: 'var(--accent)', borderRadius: '3px 3px 0 0', opacity: 0.85, height: `${Math.max(2, (d.views / maxDayViews) * 64)}px` }} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {stats.byPage.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' }}>Топ страниц</div>
                {stats.byPage.map(p => (
                  <div key={p.slug} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{p.slug || '/'}</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginLeft: '16px', flexShrink: 0 }}>{p.views.toLocaleString('ru-RU')}</div>
                  </div>
                ))}
              </div>
            )}

            {stats.byReferer.length > 0 && (
              <div>
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' }}>Источники</div>
                {stats.byReferer.map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '13px', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{r.referer || '(прямой переход)'}</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginLeft: '16px', flexShrink: 0 }}>{r.views.toLocaleString('ru-RU')}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

// ── UTM Campaign Analytics ─────────────────────────────────────────────────────

interface CampaignStats {
  byCampaign: Array<{ campaign: string | null; views: number }>
  bySource: Array<{ source: string | null; views: number }>
  byMedium: Array<{ medium: string | null; views: number }>
}

function CampaignsTab({ siteId }: { siteId: string }) {
  const [stats, setStats] = useState<CampaignStats | null>(null)
  const [days, setDays] = useState(30)
  const [loading, setLoading] = useState(false)
  const [section, setSection] = useState<'campaign' | 'source' | 'medium'>('campaign')

  useEffect(() => {
    setLoading(true)
    api.get<CampaignStats>(`/sites/${siteId}/analytics/campaigns?days=${days}`)
      .then(setStats).catch(() => setStats(null)).finally(() => setLoading(false))
  }, [siteId, days])

  const rows = !stats ? [] :
    section === 'campaign' ? stats.byCampaign.map(r => ({ label: r.campaign || '(без кампании)', views: r.views })) :
    section === 'source' ? stats.bySource.map(r => ({ label: r.source || '(без источника)', views: r.views })) :
    stats.byMedium.map(r => ({ label: r.medium || '(без medium)', views: r.views }))

  const maxViews = Math.max(1, ...rows.map(r => r.views))

  return (
    <div style={{ padding: '20px 24px' }}>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
        {DAYS_OPTIONS.map(d => (
          <button key={d} onClick={() => setDays(d)}
            style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: days === d ? 'var(--accent)' : 'var(--bg-2)', color: days === d ? '#fff' : 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
            {d}д
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '6px', marginBottom: '20px' }}>
        {(['campaign', 'source', 'medium'] as const).map(s => (
          <button key={s} onClick={() => setSection(s)}
            style={{ padding: '5px 12px', borderRadius: '7px', border: '1px solid var(--border)', background: section === s ? 'var(--accent)' : 'var(--bg-2)', color: section === s ? '#fff' : 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
            {s === 'campaign' ? 'Кампании' : s === 'source' ? 'Источники' : 'Medium'}
          </button>
        ))}
      </div>

      {loading && <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px' }}>Загрузка...</div>}
      {!loading && rows.length === 0 && (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>📊</div>
          <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '6px' }}>Нет данных UTM</div>
          <div style={{ fontSize: '12px' }}>Добавьте UTM-метки к ссылкам на ваш сайт:<br />?utm_source=google&utm_medium=cpc&utm_campaign=promo</div>
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} style={{ marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{r.label}</span>
            <span style={{ color: 'var(--text-muted)', fontWeight: 700, marginLeft: '12px', flexShrink: 0 }}>{r.views.toLocaleString('ru-RU')}</span>
          </div>
          <div style={{ height: '6px', background: 'var(--bg-3)', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(r.views / maxViews) * 100}%`, background: 'var(--accent)', borderRadius: '3px', transition: 'width 0.4s' }} />
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Conversion Funnels ─────────────────────────────────────────────────────────

interface Funnel { id: string; name: string; steps: Array<{ slug: string; label: string }>; createdAt: string }
interface FunnelStats { name: string; steps: Array<{ slug: string; label: string; visitors: number }> }

function FunnelsTab({ siteId }: { siteId: string }) {
  const [funnels, setFunnels] = useState<Funnel[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [funnelStats, setFunnelStats] = useState<FunnelStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [statsLoading, setStatsLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newSteps, setNewSteps] = useState([{ slug: '/', label: 'Главная' }, { slug: '/pricing', label: 'Цены' }])
  const [days, setDays] = useState(30)

  const loadFunnels = () => {
    setLoading(true)
    api.get<Funnel[]>(`/sites/${siteId}/analytics/funnels`)
      .then(setFunnels).catch(() => setFunnels([])).finally(() => setLoading(false))
  }

  useEffect(() => { loadFunnels() }, [siteId])

  useEffect(() => {
    if (!selected) { setFunnelStats(null); return }
    setStatsLoading(true)
    api.get<FunnelStats>(`/sites/${siteId}/analytics/funnels/${selected}/stats?days=${days}`)
      .then(setFunnelStats).catch(() => setFunnelStats(null)).finally(() => setStatsLoading(false))
  }, [selected, siteId, days])

  const handleCreate = async () => {
    if (!newName.trim() || newSteps.length < 2) return
    await api.post(`/sites/${siteId}/analytics/funnels`, { name: newName, steps: newSteps })
    setCreating(false); setNewName(''); setNewSteps([{ slug: '/', label: 'Главная' }, { slug: '/pricing', label: 'Цены' }])
    loadFunnels()
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить воронку?')) return
    await api.delete(`/sites/${siteId}/analytics/funnels/${id}`).catch(() => null)
    if (selected === id) { setSelected(null); setFunnelStats(null) }
    loadFunnels()
  }

  const addStep = () => setNewSteps(s => [...s, { slug: '/', label: `Шаг ${s.length + 1}` }])
  const removeStep = (i: number) => setNewSteps(s => s.filter((_, idx) => idx !== i))
  const updateStep = (i: number, field: 'slug' | 'label', val: string) =>
    setNewSteps(s => s.map((step, idx) => idx === i ? { ...step, [field]: val } : step))

  const maxVisitors = funnelStats ? Math.max(1, ...funnelStats.steps.map(s => s.visitors)) : 1

  return (
    <div style={{ padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Отслеживайте путь посетителей по страницам</div>
        <button onClick={() => setCreating(v => !v)}
          style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
          + Воронка
        </button>
      </div>

      {creating && (
        <div style={{ background: 'var(--bg-2)', borderRadius: '12px', padding: '16px', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>Новая воронка</div>
          <input className="prop-input" placeholder="Название (напр. «Продажа»)" value={newName} onChange={e => setNewName(e.target.value)} />
          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginTop: '4px' }}>Шаги (страницы по порядку):</div>
          {newSteps.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', width: '20px', textAlign: 'center', flexShrink: 0 }}>{i + 1}</span>
              <input className="prop-input" style={{ flex: 1 }} placeholder="slug (напр. /)" value={step.slug} onChange={e => updateStep(i, 'slug', e.target.value)} />
              <input className="prop-input" style={{ flex: 1 }} placeholder="Название шага" value={step.label} onChange={e => updateStep(i, 'label', e.target.value)} />
              {newSteps.length > 2 && (
                <button onClick={() => removeStep(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px', flexShrink: 0 }}>×</button>
              )}
            </div>
          ))}
          <button onClick={addStep} style={{ padding: '5px', background: 'none', border: '1px dashed var(--border)', borderRadius: '6px', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '12px' }}>+ Добавить шаг</button>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => setCreating(false)} style={{ padding: '6px 14px', background: 'var(--bg-3)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: '7px', fontSize: '12px', cursor: 'pointer' }}>Отмена</button>
            <button onClick={handleCreate} disabled={!newName.trim() || newSteps.length < 2}
              style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: !newName.trim() ? 0.6 : 1 }}>
              Создать
            </button>
          </div>
        </div>
      )}

      {loading && <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '32px' }}>Загрузка...</div>}
      {!loading && funnels.length === 0 && !creating && (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔻</div>
          <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Воронок нет</div>
          <div style={{ fontSize: '12px' }}>Создайте воронку, чтобы отслеживать путь посетителей</div>
        </div>
      )}

      {funnels.map(f => (
        <div key={f.id} style={{ background: selected === f.id ? 'var(--bg-2)' : 'var(--bg-3)', borderRadius: '10px', padding: '12px 16px', marginBottom: '8px', border: `1px solid ${selected === f.id ? 'var(--accent)' : 'var(--border)'}`, cursor: 'pointer' }}
          onClick={() => setSelected(selected === f.id ? null : f.id)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>{f.name}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{(f.steps as Array<{ slug: string; label: string }>).length} шагов</div>
            </div>
            <button onClick={e => { e.stopPropagation(); handleDelete(f.id) }}
              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '13px' }}>✕</button>
          </div>
        </div>
      ))}

      {selected && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
            {DAYS_OPTIONS.map(d => (
              <button key={d} onClick={() => setDays(d)}
                style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: days === d ? 'var(--accent)' : 'var(--bg-2)', color: days === d ? '#fff' : 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                {d}д
              </button>
            ))}
          </div>
          {statsLoading && <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '24px' }}>Загрузка данных...</div>}
          {!statsLoading && funnelStats && (
            <div style={{ background: 'var(--bg-2)', borderRadius: '12px', padding: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '16px' }}>{funnelStats.name}</div>
              {funnelStats.steps.map((step, i) => {
                const pct = i === 0 ? 100 : Math.round((step.visitors / (funnelStats.steps[0]?.visitors || 1)) * 100)
                const dropPct = i === 0 ? 0 : 100 - Math.round((step.visitors / (funnelStats.steps[i - 1]?.visitors || 1)) * 100)
                return (
                  <div key={i} style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'var(--accent)', color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{step.label}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{step.slug}</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--accent)' }}>{step.visitors.toLocaleString('ru-RU')}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{pct}% от входа</div>
                      </div>
                    </div>
                    <div style={{ height: '8px', background: 'var(--bg-3)', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${(step.visitors / maxVisitors) * 100}%`, background: i === 0 ? '#22c55e' : pct > 50 ? 'var(--accent)' : '#f59e0b', borderRadius: '4px', transition: 'width 0.5s' }} />
                    </div>
                    {i > 0 && dropPct > 0 && (
                      <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>↓ -{dropPct}% отсева от предыдущего шага</div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function AbTestsTab({ siteId }: { siteId: string }) {
  const [tests, setTests] = useState<ApiAbTest[]>([])
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({ name: '', pageSlug: '/', blockId: '' })
  const [saving, setSaving] = useState(false)

  const load = () => {
    setLoading(true)
    abTestsApi.list(siteId).then(setTests).catch(() => setTests([])).finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [siteId])

  const handleCreate = async () => {
    if (!form.name || !form.blockId) return
    setSaving(true)
    try {
      await abTestsApi.create(siteId, form)
      setCreating(false)
      setForm({ name: '', pageSlug: '/', blockId: '' })
      load()
    } catch { /* noop */ } finally { setSaving(false) }
  }

  const toggleStatus = async (test: ApiAbTest) => {
    const next = test.status === 'RUNNING' ? 'PAUSED' : 'RUNNING'
    await abTestsApi.updateStatus(siteId, test.id, next).catch(() => null)
    load()
  }

  const handleEnd = async (test: ApiAbTest) => {
    await abTestsApi.updateStatus(siteId, test.id, 'ENDED').catch(() => null)
    load()
  }

  const handleDelete = async (test: ApiAbTest) => {
    if (!confirm(`Удалить тест "${test.name}"?`)) return
    await abTestsApi.delete(siteId, test.id).catch(() => null)
    load()
  }

  const statusColor = (s: string) => s === 'RUNNING' ? '#22c55e' : s === 'PAUSED' ? '#f59e0b' : '#9ca3af'
  const statusLabel = (s: string) => s === 'RUNNING' ? 'Активен' : s === 'PAUSED' ? 'Пауза' : 'Завершён'

  return (
    <div style={{ padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Тестируйте варианты блоков и сравнивайте конверсии</div>
        <button
          onClick={() => setCreating(v => !v)}
          style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
          + Новый тест
        </button>
      </div>

      {creating && (
        <div style={{ background: 'var(--bg-2)', borderRadius: '12px', padding: '16px', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>Новый A/B тест</div>
          <input className="prop-input" placeholder="Название теста (напр. «Кнопка CTA»)" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <input className="prop-input" placeholder="Страница (slug, напр. /)" value={form.pageSlug} onChange={e => setForm(f => ({ ...f, pageSlug: e.target.value }))} />
          <input className="prop-input" placeholder="ID блока (из панели свойств)" value={form.blockId} onChange={e => setForm(f => ({ ...f, blockId: e.target.value }))} />
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
            Отметьте два блока как вариант A и B для этого теста в панели Свойства → A/B тест
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => setCreating(false)} style={{ padding: '6px 14px', background: 'var(--bg-3)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: '7px', fontSize: '12px', cursor: 'pointer' }}>Отмена</button>
            <button onClick={handleCreate} disabled={saving || !form.name || !form.blockId}
              style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: saving || !form.name || !form.blockId ? 0.6 : 1 }}>
              {saving ? 'Создание...' : 'Создать'}
            </button>
          </div>
        </div>
      )}

      {loading && <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px' }}>Загрузка...</div>}
      {!loading && tests.length === 0 && (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px', fontSize: '14px' }}>Нет активных тестов</div>
      )}

      {tests.map(test => {
        const { A, B } = test.stats
        const totalViews = A.views + B.views
        const winner = A.rate > B.rate ? 'A' : B.rate > A.rate ? 'B' : null
        return (
          <div key={test.id} style={{ background: 'var(--bg-2)', borderRadius: '12px', padding: '16px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>{test.name}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  <span style={{ fontFamily: 'monospace' }}>{test.pageSlug}</span> · {totalViews} просмотров
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: 600, color: statusColor(test.status), background: statusColor(test.status) + '22', padding: '3px 8px', borderRadius: '100px' }}>{statusLabel(test.status)}</span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
              {(['A', 'B'] as const).map(v => {
                const s = test.stats[v]
                const isWinner = winner === v && test.status !== 'RUNNING'
                return (
                  <div key={v} style={{ background: 'var(--bg-3)', borderRadius: '8px', padding: '12px', border: isWinner ? '2px solid #22c55e' : '2px solid transparent', position: 'relative' }}>
                    {isWinner && <div style={{ position: 'absolute', top: '6px', right: '8px', fontSize: '10px', color: '#22c55e', fontWeight: 700 }}>ПОБЕДИТЕЛЬ</div>}
                    <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--accent)', marginBottom: '2px' }}>Вариант {v}</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)' }}>{s.rate}%</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>конверсий</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>{s.views} просм · {s.conversions} конв</div>
                    <div style={{ marginTop: '8px', height: '4px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min(100, s.rate * 5)}%`, background: 'var(--accent)', borderRadius: '2px' }} />
                    </div>
                  </div>
                )
              })}
            </div>

            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
              {test.status !== 'ENDED' && (
                <>
                  <button onClick={() => toggleStatus(test)}
                    style={{ padding: '5px 12px', border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-secondary)', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>
                    {test.status === 'RUNNING' ? 'Пауза' : 'Возобновить'}
                  </button>
                  <button onClick={() => handleEnd(test)}
                    style={{ padding: '5px 12px', border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-secondary)', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>
                    Завершить
                  </button>
                </>
              )}
              <button onClick={() => handleDelete(test)}
                style={{ padding: '5px 12px', border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>
                Удалить
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

interface ClickPoint { x: number; y: number }

function HeatmapTab({ siteId }: { siteId: string }) {
  const [slug, setSlug] = useState('/')
  const [days, setDays] = useState(30)
  const [points, setPoints] = useState<ClickPoint[]>([])
  const [loading, setLoading] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setLoading(true)
    api.get<ClickPoint[]>(`/sites/${siteId}/analytics/heatmap?slug=${encodeURIComponent(slug)}&days=${days}`)
      .then(setPoints).catch(() => setPoints([])).finally(() => setLoading(false))
  }, [siteId, slug, days])

  const drawHeatmap = useCallback(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container || !points.length) return
    const W = container.clientWidth
    const H = Math.round(W * 9 / 16)
    canvas.width = W
    canvas.height = H
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const off = document.createElement('canvas')
    off.width = W
    off.height = H
    const offCtx = off.getContext('2d')!
    offCtx.clearRect(0, 0, W, H)

    const R = Math.max(20, Math.min(60, W / 20))
    for (const p of points) {
      const x = p.x * W
      const y = p.y * H
      const grad = offCtx.createRadialGradient(x, y, 0, x, y, R)
      grad.addColorStop(0, 'rgba(255,255,255,0.25)')
      grad.addColorStop(1, 'rgba(255,255,255,0)')
      offCtx.fillStyle = grad
      offCtx.beginPath()
      offCtx.arc(x, y, R, 0, Math.PI * 2)
      offCtx.fill()
    }

    const imgData = offCtx.getImageData(0, 0, W, H)
    const colorData = ctx.createImageData(W, H)
    for (let i = 0; i < imgData.data.length; i += 4) {
      const alpha = imgData.data[i + 3] / 255
      if (alpha < 0.01) continue
      let r = 0, g = 0, b = 0
      if (alpha < 0.25) { const t = alpha / 0.25; r = 0; g = Math.round(t * 255); b = 255 }
      else if (alpha < 0.5) { const t = (alpha - 0.25) / 0.25; r = 0; g = 255; b = Math.round((1 - t) * 255) }
      else if (alpha < 0.75) { const t = (alpha - 0.5) / 0.25; r = Math.round(t * 255); g = 255; b = 0 }
      else { const t = (alpha - 0.75) / 0.25; r = 255; g = Math.round((1 - t) * 255); b = 0 }
      colorData.data[i] = r
      colorData.data[i + 1] = g
      colorData.data[i + 2] = b
      colorData.data[i + 3] = Math.round(Math.min(1, alpha * 2) * 200)
    }
    ctx.putImageData(colorData, 0, 0)
  }, [points])

  useEffect(() => {
    if (points.length > 0) drawHeatmap()
  }, [points, drawHeatmap])

  return (
    <div style={{ padding: '20px 24px', flex: 1 }}>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={slug}
          onChange={e => setSlug(e.target.value)}
          placeholder="Страница (напр. /)"
          style={{ flex: 1, padding: '6px 10px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--bg-2)', color: 'var(--text-primary)', minWidth: '100px' }}
        />
        {[7, 30, 90].map(d => (
          <button key={d} onClick={() => setDays(d)}
            style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: days === d ? 'var(--accent)' : 'var(--bg-2)', color: days === d ? '#fff' : 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
            {d}д
          </button>
        ))}
      </div>

      {loading && <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>Загрузка...</div>}
      {!loading && points.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>🖱</div>
          <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '6px' }}>Нет данных кликов</div>
          <div style={{ fontSize: '12px' }}>Клики по опубликованным страницам начнут появляться здесь автоматически</div>
        </div>
      )}
      {!loading && points.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{points.length} кликов за {days} дней</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-muted)' }}>
              <span style={{ display: 'inline-block', width: '60px', height: '8px', borderRadius: '4px', background: 'linear-gradient(to right, #0000ff, #00ffff, #ffff00, #ff0000)' }} />
              <span>мало → много</span>
            </div>
          </div>
          <div ref={containerRef} style={{ position: 'relative', background: 'var(--bg-3)', borderRadius: '10px', overflow: 'hidden', border: '1px solid var(--border)', lineHeight: 0 }}>
            <div style={{ width: '100%', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '12px', position: 'absolute', inset: 0 }}>
              Область страницы (нормализованная позиция)
            </div>
            <canvas
              ref={canvasRef}
              style={{ position: 'relative', zIndex: 1, display: 'block', width: '100%', aspectRatio: '16/9' }}
            />
          </div>
          <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px' }}>
            {[...points].sort((a, b) => (b.x + b.y) - (a.x + a.y)).slice(0, 3).map((p, i) => (
              <div key={i} style={{ background: 'var(--bg-2)', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', border: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>Зона {i + 1}</div>
                <div style={{ color: 'var(--text-muted)' }}>x: {Math.round(p.x * 100)}%, y: {Math.round(p.y * 100)}%</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

interface SessionMeta { id: string; pageSlug: string; duration: number; userAgent?: string; startedAt: string }

function SessionsTab({ siteId }: { siteId: string }) {
  const [sessions, setSessions] = useState<SessionMeta[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    api.get<SessionMeta[]>(`/sites/${siteId}/analytics/sessions`)
      .then(setSessions).catch(() => setSessions([])).finally(() => setLoading(false))
  }, [siteId])

  const fmtDuration = (s: number) => s < 60 ? `${s}с` : `${Math.floor(s / 60)}м ${s % 60}с`
  const fmtDate = (d: string) => new Date(d).toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  const browserName = (ua?: string) => {
    if (!ua) return 'Неизвестный'
    if (ua.includes('Chrome')) return 'Chrome'
    if (ua.includes('Firefox')) return 'Firefox'
    if (ua.includes('Safari')) return 'Safari'
    if (ua.includes('Edge')) return 'Edge'
    return 'Другой'
  }
  const deviceType = (ua?: string) => {
    if (!ua) return ''
    if (/Mobile|Android|iPhone|iPad/.test(ua)) return '📱'
    return '🖥'
  }

  const handleDelete = async (id: string) => {
    await api.delete(`/sites/${siteId}/analytics/sessions/${id}`).catch(() => null)
    setSessions(s => s.filter(x => x.id !== id))
  }

  return (
    <div style={{ padding: '20px 24px' }}>
      <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
        Записи сессий посетителей (rrweb). Хранится до 200 последних сессий.
      </div>
      {loading && <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px' }}>Загрузка...</div>}
      {!loading && sessions.length === 0 && (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>📹</div>
          <div>Нет записей. Сессии появятся после первых визитов на опубликованный сайт.</div>
        </div>
      )}
      {sessions.map(s => (
        <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: 'var(--bg-2)', borderRadius: '8px', marginBottom: '8px' }}>
          <div style={{ fontSize: '18px', flexShrink: 0 }}>{deviceType(s.userAgent)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.pageSlug || '/'}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
              {fmtDate(s.startedAt)} · {fmtDuration(s.duration)} · {browserName(s.userAgent)}
            </div>
          </div>
          <button onClick={() => handleDelete(s.id)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '13px', flexShrink: 0 }} title="Удалить">×</button>
        </div>
      ))}
    </div>
  )
}

type Tab = 'traffic' | 'heatmap' | 'campaigns' | 'funnels' | 'ab' | 'sessions'

export function AnalyticsPanel({ siteId, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('traffic')
  const [reportSending, setReportSending] = useState(false)
  const [reportSent, setReportSent] = useState(false)

  const handleSendReport = async () => {
    if (!siteId || reportSending) return
    setReportSending(true)
    try {
      await api.post(`/sites/${siteId}/analytics/weekly-report`, {})
      setReportSent(true)
      setTimeout(() => setReportSent(false), 4000)
    } catch { /* ignore */ } finally {
      setReportSending(false)
    }
  }

  const tabs: [Tab, string][] = [
    ['traffic', 'Трафик'],
    ['heatmap', '🔥 Хитмэп'],
    ['campaigns', '📊 Кампании'],
    ['funnels', '🔻 Воронки'],
    ['ab', 'A/B тесты'],
    ['sessions', '📹 Записи'],
  ]

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ width: '640px', maxWidth: '95vw', height: '100vh', background: 'var(--bg-1)', borderLeft: '1px solid var(--border)', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '20px 24px 0', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-1)', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>Аналитика</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {siteId && (
                <button onClick={handleSendReport} disabled={reportSending || !siteId}
                  title="Отправить еженедельный отчёт на email"
                  style={{ padding: '5px 12px', background: reportSent ? '#22c55e20' : 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', color: reportSent ? '#22c55e' : 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                  {reportSent ? '✓ Отчёт отправлен' : reportSending ? '...' : '📧 Отчёт на email'}
                </button>
              )}
              <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0', overflowX: 'auto' }}>
            {tabs.map(([t, label]) => (
              <button key={t} onClick={() => setTab(t)}
                style={{ padding: '8px 14px', border: 'none', background: 'none', fontSize: '12px', fontWeight: 600, color: tab === t ? 'var(--accent)' : 'var(--text-muted)', borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent', cursor: 'pointer', marginBottom: '-1px', whiteSpace: 'nowrap' }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {siteId ? (
          tab === 'traffic' ? <TrafficTab siteId={siteId} /> :
          tab === 'heatmap' ? <HeatmapTab siteId={siteId} /> :
          tab === 'campaigns' ? <CampaignsTab siteId={siteId} /> :
          tab === 'funnels' ? <FunnelsTab siteId={siteId} /> :
          tab === 'ab' ? <AbTestsTab siteId={siteId} /> :
          <SessionsTab siteId={siteId} />
        ) : (
          <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '60px' }}>Сохраните сайт для просмотра аналитики</div>
        )}
      </div>
    </div>
  )
}
